import { useEffect, useRef } from 'react';

interface PerformanceMetrics {
  lcp: number;
  fid: number;
  cls: number;
  fcp: number;
  ttfb: number;
}

/**
 * Performance Monitor Component
 * Tracks Core Web Vitals and other performance metrics
 */
export function PerformanceMonitor() {
  const metricsRef = useRef<Partial<PerformanceMetrics>>({});

  useEffect(() => {
    // Track Largest Contentful Paint (LCP)
    const observeLCP = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          metricsRef.current.lcp = lastEntry.startTime;
          
          // Log if LCP is poor (> 2.5s)
          if (lastEntry.startTime > 2500) {
            console.warn(`Poor LCP: ${lastEntry.startTime}ms (should be < 2500ms)`);
          }
        });
        
        try {
          observer.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch (e) {
          console.warn('LCP observation not supported');
        }
      }
    };

    // Track First Input Delay (FID)
    const observeFID = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            metricsRef.current.fid = entry.processingStart - entry.startTime;
            
            // Log if FID is poor (> 100ms)
            if (entry.processingStart - entry.startTime > 100) {
              console.warn(`Poor FID: ${entry.processingStart - entry.startTime}ms (should be < 100ms)`);
            }
          });
        });
        
        try {
          observer.observe({ entryTypes: ['first-input'] });
        } catch (e) {
          console.warn('FID observation not supported');
        }
      }
    };

    // Track Cumulative Layout Shift (CLS)
    const observeCLS = () => {
      if ('PerformanceObserver' in window) {
        let clsValue = 0;
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
              metricsRef.current.cls = clsValue;
              
              // Log if CLS is poor (> 0.1)
              if (clsValue > 0.1) {
                console.warn(`Poor CLS: ${clsValue} (should be < 0.1)`);
              }
            }
          });
        });
        
        try {
          observer.observe({ entryTypes: ['layout-shift'] });
        } catch (e) {
          console.warn('CLS observation not supported');
        }
      }
    };

    // Track First Contentful Paint (FCP)
    const observeFCP = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            metricsRef.current.fcp = entry.startTime;
            
            // Log if FCP is poor (> 1.8s)
            if (entry.startTime > 1800) {
              console.warn(`Poor FCP: ${entry.startTime}ms (should be < 1800ms)`);
            }
          });
        });
        
        try {
          observer.observe({ entryTypes: ['paint'] });
        } catch (e) {
          console.warn('FCP observation not supported');
        }
      }
    };

    // Track Time to First Byte (TTFB)
    const observeTTFB = () => {
      if ('performance' in window && 'getEntriesByType' in performance) {
        const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (navigationEntries.length > 0) {
          const ttfb = navigationEntries[0].responseStart - navigationEntries[0].requestStart;
          metricsRef.current.ttfb = ttfb;
          
          // Log if TTFB is poor (> 600ms)
          if (ttfb > 600) {
            console.warn(`Poor TTFB: ${ttfb}ms (should be < 600ms)`);
          }
        }
      }
    };

    // Track resource loading performance
    const observeResources = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            // Log slow resources (> 1s)
            if (entry.duration > 1000) {
              console.warn(`Slow resource: ${entry.name} took ${entry.duration}ms`);
            }
          });
        });
        
        try {
          observer.observe({ entryTypes: ['resource'] });
        } catch (e) {
          console.warn('Resource observation not supported');
        }
      }
    };

    // Track long tasks that block the main thread
    const observeLongTasks = () => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            console.warn(`Long task detected: ${entry.duration}ms (blocks main thread)`);
          });
        });
        
        try {
          observer.observe({ entryTypes: ['longtask'] });
        } catch (e) {
          console.warn('Long task observation not supported');
        }
      }
    };

    // Initialize all observers
    observeLCP();
    observeFID();
    observeCLS();
    observeFCP();
    observeTTFB();
    observeResources();
    observeLongTasks();

    // Report metrics after page load
    const reportMetrics = () => {
      setTimeout(() => {
        const metrics = metricsRef.current;
        console.group('🚀 Performance Metrics');
        console.log('LCP (Largest Contentful Paint):', metrics.lcp ? `${metrics.lcp}ms` : 'Not measured');
        console.log('FID (First Input Delay):', metrics.fid ? `${metrics.fid}ms` : 'Not measured');
        console.log('CLS (Cumulative Layout Shift):', metrics.cls ? metrics.cls.toFixed(3) : 'Not measured');
        console.log('FCP (First Contentful Paint):', metrics.fcp ? `${metrics.fcp}ms` : 'Not measured');
        console.log('TTFB (Time to First Byte):', metrics.ttfb ? `${metrics.ttfb}ms` : 'Not measured');
        console.groupEnd();

        // Calculate performance score
        let score = 100;
        if (metrics.lcp && metrics.lcp > 2500) score -= 25;
        if (metrics.fid && metrics.fid > 100) score -= 25;
        if (metrics.cls && metrics.cls > 0.1) score -= 25;
        if (metrics.fcp && metrics.fcp > 1800) score -= 25;

        console.log(`📊 Estimated Performance Score: ${score}%`);
        
        // Send metrics to analytics (if needed)
        if (window.gtag) {
          window.gtag('event', 'performance_metrics', {
            lcp: metrics.lcp,
            fid: metrics.fid,
            cls: metrics.cls,
            fcp: metrics.fcp,
            ttfb: metrics.ttfb,
            score: score
          });
        }
      }, 5000);
    };

    // Report metrics when page is fully loaded
    if (document.readyState === 'complete') {
      reportMetrics();
    } else {
      window.addEventListener('load', reportMetrics);
    }

    // Memory usage monitoring
    const monitorMemory = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        if (memory.usedJSHeapSize > 50 * 1024 * 1024) { // 50MB
          console.warn(`High memory usage: ${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`);
        }
      }
    };

    const memoryInterval = setInterval(monitorMemory, 30000); // Check every 30s

    return () => {
      clearInterval(memoryInterval);
      window.removeEventListener('load', reportMetrics);
    };
  }, []);

  return null;
}

export default PerformanceMonitor;